/**
 * OmniLeagues — Program History dashboard (AD role).
 *
 * Data + editing model:
 *   • Season records — live from `season_archives`; the AD edits those inline
 *     on the STATISTICS tab and the numbers here update automatically.
 *   • Timeline + 1000+ Point Club — editable RIGHT HERE (pencil icons).
 *     AD edits are stamped `edited_by_ad` and survive sheet re-syncs.
 *   • "Sync master sheet" re-pulls the Google Sheet for game logs / records.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Trophy, Flame, History, Medal, Loader2, Crown,
  Pencil, Check, X, Plus, Trash2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EDIT_ROLES = ['company_support', 'ad', 'league_admin'];

const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US',
  { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

export default function ProgramHistoryPanel({ orgId, userRole }) {
  const { isDark } = useTheme();
  const canEdit = EDIT_ROLES.includes(userRole);
  const [overview, setOverview] = useState(null);
  const [streaks, setStreaks] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sport, setSport] = useState('basketball');

  const base = `${API_URL}/api/program-history/${orgId}`;

  const fetchAll = (sp = sport) => Promise.all([
    axios.get(`${base}/overview?sport=${sp}`),
    axios.get(`${base}/streaks`),
    axios.get(`${base}/leaders`),
  ]).then(([o, s, l]) => {
    setOverview(o.data);
    setStreaks(s.data);
    setLeaders(l.data.leaders || []);
  });

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    fetchAll().catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, sport]);


  const card = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';
  const text = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const inputCls = isDark ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300';

  if (loading) {
    return <div className="flex justify-center py-12" data-testid="history-loading"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;
  }
  if (!overview) {
    return <Card className={card}><CardContent className={`p-8 text-center ${muted}`}>No program history data available.</CardContent></Card>;
  }

  const at = overview.alltime || {};
  const ch = overview.championships || {};

  return (
    <div className="space-y-6" data-testid="program-history-panel">
      {/* ── 1. HERO ─────────────────────────────────────────────────── */}
      <Card className={card}>
        <CardContent className="py-6">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <History className="h-5 w-5 text-amber-500" />
                <h2 className={`text-lg font-semibold ${text}`}>Program History</h2>
 {/* "Sync master sheet" removed (AJ 2026-10-18): it re-imported the old history sheet over the live game data. */}
              </div>
              <p className={`text-sm ${muted}`}>
                {overview.seasons_count} seasons · {overview.first_season} to {overview.latest_season}
              </p>
              <div className="flex gap-1.5 mt-2" data-testid="history-sport-toggle">
                {[['basketball', 'Basketball'], ['volleyball', 'Volleyball'], ['baseball', 'Baseball']].map(([sp, lbl]) => (
                  <button key={sp} onClick={() => setSport(sp)}
                    data-testid={`history-sport-${sp}`}
                    className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${sport === sp
                      ? 'bg-amber-500 text-slate-900'
                      : (isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              {sport !== 'basketball' && (
                <p className={`text-xs mt-2 ${muted}`} data-testid="history-sport-note">
                  {overview.seasons_count > 0
                    ? `${sport === 'volleyball' ? 'Volleyball' : 'Baseball'} records build automatically from loaded game results.`
                    : `No ${sport} results loaded yet — records will build automatically as games are finalized.`}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-8">
              <HeroStat label="All-time record" value={`${at.wins}-${at.losses}`} sub={`${at.win_pct}%`} accent="text-amber-500" testId="history-alltime-record" />
              <HeroStat label="Games logged" value={overview.total_games} sub="2014-15 to present" accent={text} testId="history-total-games" />
              <HeroStat label="Regional titles" value={ch.regionals} sub="team championships" accent="text-blue-400" testId="history-regionals" />
              <HeroStat label="State titles" value={ch.states} sub="team championships" accent="text-emerald-400" testId="history-states" />
              <HeroStat label="National titles" value={ch.hspn} sub="HSPN championships" accent="text-purple-400" testId="history-nationals" />
              <HeroStat label="Grand Champions" value={ch.grand} sub="HSPN top bracket" accent="text-rose-400" testId="history-grand-champions" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── 2. ALL-TIME BY TEAM ─────────────────────────────────────── */}
      <Card className={card} data-testid="history-alltime-card">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className={`text-sm font-medium flex items-center gap-2 ${text}`}>
              <Trophy className="h-4 w-4 text-amber-500" /> All-time records by team
            </CardTitle>
            {canEdit && (
              <span className={`text-[11px] ${muted}`} data-testid="history-records-hint">
                Wrong number? Fix the season on the <strong>Statistics</strong> tab — totals here update automatically.
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {(overview.per_level || []).map(t => (
              <div key={t.level}
                className={`rounded-lg border p-3 ${isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`}
                data-testid={`history-level-${t.level}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-semibold ${muted}`}>{t.label}</span>
                  <Badge variant="outline" className="text-[9px] border-slate-500/40">{t.level}</Badge>
                </div>
                <div className="text-xl font-extrabold text-amber-500">{t.wins}-{t.losses}</div>
                <div className={`text-xs ${muted}`}>{t.win_pct}% · {t.seasons_played} seasons</div>
                {t.best_season && (
                  <div className={`text-[10px] mt-1.5 ${muted}`}>
                    Best: <span className={text}>{t.best_season.record}</span> ({t.best_season.season})
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── 3. WIN STREAKS ──────────────────────────────────────────── */}
      {sport === 'basketball' && (<>
      <Card className={card} data-testid="history-streaks-card">
        <CardHeader className="pb-3">
          <CardTitle className={`text-sm font-medium flex items-center gap-2 ${text}`}>
            <Flame className="h-4 w-4 text-orange-500" /> Longest win streaks
            <span className={`text-[10px] font-normal ${muted}`}>computed from the game logs</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {streaks?.program && (
            <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${isDark ? 'bg-orange-500/5 border-orange-500/30' : 'bg-orange-50 border-orange-200'}`}
              data-testid="history-program-streak">
              <Crown className="h-5 w-5 text-orange-500" />
              <div>
                <span className="text-lg font-extrabold text-orange-500">{streaks.program.length} straight wins</span>
                <span className={`text-xs ml-2 ${muted}`}>
                  program-wide · {fmtDate(streaks.program.start_date)} → {fmtDate(streaks.program.end_date)}
                </span>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {(streaks?.by_level || []).map(s => (
              <div key={s.level} className={`flex items-center justify-between rounded-md border px-3 py-2 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                data-testid={`history-streak-${s.level}`}>
                <div>
                  <span className={`text-xs font-semibold ${text}`}>{s.label}</span>
                  <div className={`text-[10px] ${muted}`}>{fmtDate(s.start_date)} → {fmtDate(s.end_date)}</div>
                </div>
                <span className="text-lg font-extrabold text-orange-500">{s.length}W</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── 5. 1000+ POINT CLUB (editable) ──────────────────────────── */}
      <LeadersCard
        leaders={leaders}
        canEdit={canEdit}
        base={base}
        onChanged={setLeaders}
        ui={{ card, text, muted, inputCls, isDark }}
      />
      </>)}

      {/* ── 4. SEASON TIMELINE (editable) — every sport (AJ 2026-10-18): the title
              counts above, the About page and the home stats counter read it ── */}
      <TimelineCard
        key={sport}
        sport={sport}
        timeline={overview.timeline || []}
        canEdit={canEdit}
        base={base}
        onSaved={fetchAll}
        ui={{ card, text, muted, inputCls, isDark }}
      />
    </div>
  );
}

function HeroStat({ label, value, sub, accent, testId }) {
  return (
    <div data-testid={testId}>
      <div className={`text-2xl font-extrabold ${accent}`}>{value ?? '—'}</div>
      <div className="text-xs font-medium text-slate-500">{label}</div>
      {sub && <div className="text-[10px] text-slate-500/70">{sub}</div>}
    </div>
  );
}

/* ── Season timeline with inline editing ──────────────────────────────── */
function TimelineCard({ sport = 'basketball', timeline, canEdit, base, onSaved, ui }) {
  const { card, text, muted, inputCls, isDark } = ui;
  const [editing, setEditing] = useState(null);   // season string
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const startEdit = (t) => {
    setEditing(t.season);
    setForm({
      milestones: t.milestones || '',
      new_team: t.achievements?.new_team || '',
      regionals: t.achievements?.regionals || '',
      states: t.achievements?.states || '',
      hspn: t.achievements?.hspn || '',
      hspn_3pt_skills: t.achievements?.hspn_3pt_skills || '',
    });
  };

  const startEditNew = () => {
    setEditing('__new__');
    setForm({
      season: '', milestones: '',
      new_team: '', regionals: '', states: '', hspn: '', hspn_3pt_skills: '',
    });
  };

  const create = async () => {
    if (!(form.season || '').trim()) {
      toast.error('Season is required (e.g. 2026-2027)');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${base}/timeline`, {
        sport,
        season: form.season.trim(),
        milestones: form.milestones,
        achievements: {
          new_team: form.new_team, regionals: form.regionals,
          states: form.states, hspn: form.hspn,
          hspn_3pt_skills: form.hspn_3pt_skills,
        },
      });
      toast.success(`${form.season.trim()} added`);
      setEditing(null);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Add failed');
    } finally {
      setSaving(false);
    }
  };

  const save = async (season) => {
    setSaving(true);
    try {
      await axios.patch(`${base}/timeline/${season}?sport=${sport}`, {
        milestones: form.milestones,
        achievements: {
          new_team: form.new_team, regionals: form.regionals,
          states: form.states, hspn: form.hspn,
          hspn_3pt_skills: form.hspn_3pt_skills,
        },
      });
      toast.success(`${season} updated`);
      setEditing(null);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className={card} data-testid="history-timeline-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className={`text-sm font-medium flex items-center gap-2 ${text}`}>
            <Medal className="h-4 w-4 text-emerald-500" /> Season-by-season timeline
          </CardTitle>
          {canEdit && (
            <Button size="sm" variant="outline" className="h-7" onClick={startEditNew} data-testid="timeline-add-btn">
              <Plus className="h-3 w-3 mr-1" /> Add season
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-0">
          {editing === '__new__' && (
            <div className="flex gap-4 py-3 border-b border-emerald-500/30" data-testid="timeline-new-editor">
              <div className="w-28 flex-shrink-0">
                <Input value={form.season}
                  onChange={e => setForm(f => ({ ...f, season: e.target.value }))}
                  placeholder="2026-2027"
                  className={`h-7 text-xs font-bold ${inputCls}`}
                  data-testid="timeline-new-season-input" />
                <div className={`text-[9px] mt-1 ${muted}`}>New season</div>
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <Textarea value={form.milestones}
                  onChange={e => setForm(f => ({ ...f, milestones: e.target.value }))}
                  className={`text-xs min-h-[60px] ${inputCls}`}
                  placeholder="Season milestones…"
                  data-testid="timeline-new-milestones-input" />
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {[['new_team', 'New team'], ['regionals', 'Regionals'], ['states', 'States'],
                    ['hspn', 'Nationals'], ['hspn_3pt_skills', '3pt / Skills']].map(([k, label]) => (
                    <div key={k}>
                      <div className={`text-[9px] uppercase tracking-wide font-semibold mb-0.5 ${muted}`}>{label}</div>
                      <Input value={form[k]}
                        onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                        className={`h-7 text-xs ${inputCls}`}
                        data-testid={`timeline-new-${k}-input`} />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={create} disabled={saving} data-testid="timeline-new-save-btn">
                    {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />} Add
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditing(null)} data-testid="timeline-new-cancel-btn">
                    <X className="h-3 w-3 mr-1" /> Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
          {timeline.map((t, i) => {
            const ach = t.achievements || {};
            const isEditing = editing === t.season;
            return (
              <div key={t.season}
                className={`flex gap-4 py-3 ${i < timeline.length - 1 ? `border-b ${isDark ? 'border-slate-700/60' : 'border-slate-100'}` : ''}`}
                data-testid={`history-season-${t.season}`}>
                <div className="w-28 flex-shrink-0">
                  <div className={`text-sm font-bold ${text}`}>{t.season}</div>
                  <div className={`text-[10px] ${muted}`}>Season {t.season_number}</div>
                  {t.edited_by_ad && <Badge className="mt-1 bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[8px]">edited</Badge>}
                </div>
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="space-y-2" data-testid={`timeline-edit-${t.season}`}>
                      <Textarea value={form.milestones}
                        onChange={e => setForm(f => ({ ...f, milestones: e.target.value }))}
                        className={`text-xs min-h-[60px] ${inputCls}`}
                        placeholder="Season milestones…"
                        data-testid="timeline-milestones-input" />
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        {[['new_team', 'New team'], ['regionals', 'Regionals'], ['states', 'States'],
                          ['hspn', 'Nationals'], ['hspn_3pt_skills', '3pt / Skills']].map(([k, label]) => (
                          <div key={k}>
                            <div className={`text-[9px] uppercase tracking-wide font-semibold mb-0.5 ${muted}`}>{label}</div>
                            <Input value={form[k]}
                              onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                              className={`h-7 text-xs ${inputCls}`}
                              data-testid={`timeline-${k}-input`} />
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => save(t.season)} disabled={saving} data-testid="timeline-save-btn">
                          {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />} Save
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditing(null)} data-testid="timeline-cancel-btn">
                          <X className="h-3 w-3 mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {t.milestones && <p className={`text-xs whitespace-pre-line ${text}`}>{t.milestones}</p>}
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {ach.new_team && <Badge className="bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 text-[9px]">NEW: {ach.new_team}</Badge>}
                        {ach.regionals && <Badge className="bg-blue-500/15 text-blue-400 border border-blue-500/30 text-[9px]">Regionals: {ach.regionals.toUpperCase()}</Badge>}
                        {ach.states && <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[9px]">States: {ach.states.toUpperCase()}</Badge>}
                        {ach.hspn && <Badge className="bg-purple-500/15 text-purple-400 border border-purple-500/30 text-[9px]">Nationals: {ach.hspn.toUpperCase()}</Badge>}
                        {ach.hspn && ach.hspn.includes('***') && (
                          <Badge className="bg-rose-500/15 text-rose-400 border border-rose-500/30 text-[9px]">
                            Grand Champion: {ach.hspn.split(',').filter(x => x.includes('***')).map(x => x.replace(/\*/g, '').trim().toUpperCase()).join(', ')}
                          </Badge>
                        )}
                        {ach.hspn_3pt_skills && <Badge variant="outline" className={`text-[9px] ${muted}`}>3pt/Skills: {ach.hspn_3pt_skills}</Badge>}
                      </div>
                    </>
                  )}
                </div>
                {canEdit && !isEditing && (
                  <button onClick={() => startEdit(t)}
                    className={`self-start p-1 rounded hover:bg-slate-500/10 ${muted}`}
                    title={`Edit ${t.season}`}
                    data-testid={`timeline-edit-btn-${t.season}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/* ── 1000+ Point Club with add / edit / delete ────────────────────────── */
function LeadersCard({ leaders, canEdit, base, onChanged, ui }) {
  const { card, text, muted, inputCls, isDark } = ui;
  const [editing, setEditing] = useState(null);   // leader id or 'new'
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const blank = { player: '', first_season: '', last_season: '', total: '', active: false };

  const startEdit = (p) => {
    setEditing(p ? p.id : 'new');
    setForm(p ? { player: p.player, first_season: p.first_season || '', last_season: p.last_season || '', total: p.total ?? '', active: !!p.active } : blank);
  };

  const persist = async () => {
    setSaving(true);
    const payload = {
      player: form.player,
      first_season: form.first_season || null,
      last_season: form.last_season || null,
      total: /^\d+$/.test(String(form.total).trim()) ? parseInt(form.total, 10) : form.total,
      active: form.active,
    };
    try {
      const { data } = editing === 'new'
        ? await axios.post(`${base}/leaders`, payload)
        : await axios.patch(`${base}/leaders/${editing}`, payload);
      onChanged(data.leaders || []);
      toast.success(editing === 'new' ? `${form.player} added` : `${form.player} updated`);
      setEditing(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.player} from the scoring leaders?`)) return;
    try {
      const { data } = await axios.delete(`${base}/leaders/${p.id}`);
      onChanged(data.leaders || []);
      toast.success(`${p.player} removed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const editorRow = (
    <tr className={isDark ? 'bg-slate-900/60' : 'bg-amber-50/60'} data-testid="leader-editor-row">
      <td className="py-2 px-2" />
      <td className="py-2 px-2">
        <Input value={form.player} placeholder="Player name"
          onChange={e => setForm(f => ({ ...f, player: e.target.value }))}
          className={`h-7 text-xs ${inputCls}`} data-testid="leader-player-input" />
        <label className={`flex items-center gap-1.5 mt-1 text-[10px] ${muted}`}>
          <input type="checkbox" checked={form.active}
            onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
            data-testid="leader-active-input" />
          Still active (points continuing)
        </label>
      </td>
      <td className="py-2 px-1">
        <Input value={form.first_season} placeholder="2022-2023"
          onChange={e => setForm(f => ({ ...f, first_season: e.target.value }))}
          className={`h-7 text-xs text-center ${inputCls}`} data-testid="leader-first-input" />
      </td>
      <td className="py-2 px-1">
        <Input value={form.last_season} placeholder="2025-2026"
          onChange={e => setForm(f => ({ ...f, last_season: e.target.value }))}
          className={`h-7 text-xs text-center ${inputCls}`} data-testid="leader-last-input" />
      </td>
      <td className="py-2 px-2">
        <div className="flex items-center gap-1.5 justify-end">
          <Input value={form.total} placeholder="1234"
            onChange={e => setForm(f => ({ ...f, total: e.target.value }))}
            className={`h-7 w-20 text-xs text-right ${inputCls}`} data-testid="leader-total-input" />
          <Button size="sm" className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={persist} disabled={saving || !form.player} data-testid="leader-save-btn">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          </Button>
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditing(null)} data-testid="leader-cancel-btn">
            <X className="h-3 w-3" />
          </Button>
        </div>
      </td>
    </tr>
  );

  return (
    <Card className={card} data-testid="history-leaders-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className={`text-sm font-medium flex items-center gap-2 ${text}`}>
            <Crown className="h-4 w-4 text-amber-500" /> Career Scoring Leaders
            <span className={`text-[10px] font-normal ${muted}`}>1000+ club highlighted</span>
          </CardTitle>
          {canEdit && (
            <Button size="sm" variant="outline" className="h-7" onClick={() => startEdit(null)} data-testid="leader-add-btn">
              <Plus className="h-3 w-3 mr-1" /> Add player
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leaders-table">
            <thead>
              <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <th className={`text-left py-2 px-2 font-medium ${muted}`}>#</th>
                <th className={`text-left py-2 px-2 font-medium ${muted}`}>Player</th>
                <th className={`text-center py-2 px-1 font-medium ${muted}`}>First season</th>
                <th className={`text-center py-2 px-1 font-medium ${muted}`}>Last season</th>
                <th className={`text-right py-2 px-2 font-medium ${muted}`}>Career points</th>
              </tr>
            </thead>
            <tbody>
              {editing === 'new' && editorRow}
              {leaders.map(p => (
                editing === p.id ? React.cloneElement(editorRow, { key: p.id }) : (
                  <tr key={p.id || p.rank} className={`group border-b ${isDark ? 'border-slate-700/40' : 'border-slate-100'}`}
                    data-testid={`leader-row-${p.rank}`}>
                    <td className={`py-1.5 px-2 ${muted}`}>{p.rank}</td>
                    <td className={`py-1.5 px-2 font-medium ${text}`}>
                      {p.player}
                      {p.active && <Badge className="ml-1.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[8px]">Active</Badge>}
                      {p.edited_by_ad && <Badge className="ml-1 bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[8px]">edited</Badge>}
                    </td>
                    <td className={`py-1.5 px-1 text-center text-xs ${muted}`}>{p.first_season || '—'}</td>
                    <td className={`py-1.5 px-1 text-center text-xs ${muted}`}>{p.last_season || '—'}</td>
                    <td className="py-1.5 px-2 text-right">
                      <span className={`font-extrabold ${(typeof p.total === 'number' ? p.total : parseInt(String(p.total), 10) || 0) >= 1000 ? 'text-amber-500' : muted}`}>
                        {typeof p.total === 'number' ? p.total.toLocaleString() : p.total}
                      </span>
                      {canEdit && (
                        <span className="inline-flex ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => startEdit(p)} className={`p-0.5 ${muted} hover:text-amber-500`}
                            title="Edit" data-testid={`leader-edit-btn-${p.rank}`}>
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button onClick={() => remove(p)} className={`p-0.5 ${muted} hover:text-red-500`}
                            title="Remove" data-testid={`leader-delete-btn-${p.rank}`}>
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
        {canEdit && (
          <p className={`text-[10px] mt-2 ${muted}`}>
            Active players' career totals keep growing — update their points here at any time. Your edits survive master-sheet syncs.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
